import type { TMetricTypes, TSelectedTypeUnits } from "../types";

export interface NotificationData {
  title: string;
  body: string;
  priority: string;
  notificationId: string;
  notificationType: string;
}

export enum FirebaseEventEnum {
  PAGE_VIEW = "$pageview",
  BUTTON_CLICK = "button_click",
  HEALTH_METRICS_LOG = "health_metrics_log",
  HEALTH_PASSPORT_LOG = "health_passport_log",
  PREGNANCY_EVENT_LOG = "pregnancy_event_log",
  NEW_PREGNANCY_CYCLE = "new_pregnancy_cycle",
  NEW_SUBSCRIPTION = "new_subscription",
  FIRST_TIME_SUBSCRIPTION = "first_time_subscription",
  BLA_LOG = "bla_log",
  MINDFULNESS_ASSESSMENT = "mindfullness_assessment",
  NEW_CALENDAR_EVENT = "new_calendar_event",
  NEW_SYMPTOM_LOG = "new_symptom_log",
  USER_LOGOUT = "user_logout",
  ACCOUNT_VERIFICATION_COMPLETE = "account_verification",
  ACCOUNT_REGISTRATION_COMPLETE = "account_registration",
  PROFILE_UPDATE_COMPLETE = "profile_update",
  USER_LOGIN = "user_login",
  USER_SIGNUP = "user_signup",
  USER_PASSWORD_RESET = "user_password_reset",
  BLA_SUBMIT_LOG = "bla_submit_log",
  DELETE_CALENDAR_EVENT = "delete_calendar_event",
  UPDATE_CALENDAR_EVENT = "update_calendar_event",
  CANCEL_CALENDAR_EVENT = "cancel_calendar_event",
  USER_PROFILE_UPDATE = "user_profile_update",
  INITIALIZED_PAYMENT = "initialized_payment",
  PAYMENT_SUCCESS = "payment_success",
  PAYMENT_FAILURE = "payment_failure",
  PAYMENT_CANCELLED = "payment_cancelled",
  UPDATE_HEALTH_METRICS = "update_health_metrics",
  UPDATE_HEALTH_PASSPORT = "update_health_passport",
  UPDATE_PREGNANCY_CYCLE = "update_pregnancy_cycle",
  UPDATE_PREGNANCY_LOG_EVENT = "update_pregnancy_log_event",
  HEALTH_METRICS_DELETE = "health_metrics_delete",
  HEALTH_PASSPORT_DELETE = "health_passport_delete",
  PREGNANCY_CYCLE_DELETE = "pregnancy_cycle_delete",
  PREGNANCY_LOG_EVENT_DELETE = "pregnancy_log_event_delete",

  NEW_CHAT_ROOM = "new_chat_room",
  NEW_MESSAGE = "new_chat_message",
  READ_CHAT_MESSAGE = "read_chat_message",
  REDEEM_VOUCHER = "redeem_voucher",
  RENEW_SUBSCRIPTION = "renew_subscription",
  SUBSCRIBE_PROGRAM = "subscribe_program",
  SELECT_PROGRAM_PLAN = "select_program_plan",
  NEW_INCOMING_NOTIFICATION = "new_incoming_notification",

  // KEM
  KEM_CHAT_MESSAGE = "kem_chat_message",
  KEM_SPEECH_START_RECORDING = "kem_speech_start_recording",
  KEM_SPEECH_SEND_RECORDING = "kem_speech_send_recording",
  KEM_INTERACTION_TIME = "kem_interaction_time",
  KEM_IC_CHAT_MESSAGE = "kem_ic_chat_message",
  KEM_COACH_CHAT_MESSAGE = "kem_coach_chat_message",

  UTM_SOURCE = "utm_source",
}

export interface FirebaseParamMap {
  [FirebaseEventEnum.PAGE_VIEW]: {
    $current_url: string;
  };
  [FirebaseEventEnum.BUTTON_CLICK]: {
    button_name: string;
  };
  [FirebaseEventEnum.NEW_CALENDAR_EVENT]: {
    eventType: string; // TODO: Update this to type
  };
  [FirebaseEventEnum.NEW_SYMPTOM_LOG]: Record<string, unknown>;
  [FirebaseEventEnum.USER_LOGOUT]: Record<string, unknown>;
  [FirebaseEventEnum.ACCOUNT_VERIFICATION_COMPLETE]: Record<string, unknown>;
  [FirebaseEventEnum.NEW_PREGNANCY_CYCLE]: {
    ancDay?: string;
    expectedDeliveryDate?: string;
  };
  [FirebaseEventEnum.PREGNANCY_EVENT_LOG]: {
    event: string; // TODO: Update this type
  };
  [FirebaseEventEnum.ACCOUNT_REGISTRATION_COMPLETE]: Record<string, unknown>;
  [FirebaseEventEnum.PROFILE_UPDATE_COMPLETE]: {
    userId: string;
  };
  [FirebaseEventEnum.HEALTH_METRICS_LOG]: {
    metricsType: TMetricTypes;
    value: number | string;
    unit?: TSelectedTypeUnits<TMetricTypes>;
    extra?: Record<string, unknown>;
  };
  [FirebaseEventEnum.HEALTH_PASSPORT_LOG]: {
    passportType: string;
    value: string;
    extra?: Record<string, unknown>;
  };
  [FirebaseEventEnum.NEW_SUBSCRIPTION]: {
    userSubscriptionId: string;
    programId: string;
    planId: string;
    planName: string;
    programName: string;
  };
  [FirebaseEventEnum.BLA_LOG]: {
    recordId: string;
  };
  [FirebaseEventEnum.MINDFULNESS_ASSESSMENT]: {
    assessmentType: "PHQ-9" | "GAD-7";
    score: number;
  };
  [FirebaseEventEnum.USER_LOGIN]: {
    email?: string;
    phone?: string;
  };
  [FirebaseEventEnum.USER_SIGNUP]: {
    email?: string;
    phone?: string;
  };
  [FirebaseEventEnum.BLA_SUBMIT_LOG]: Record<string, unknown>;
  [FirebaseEventEnum.DELETE_CALENDAR_EVENT]: {
    id?: string;
  };
  [FirebaseEventEnum.UPDATE_CALENDAR_EVENT]: {
    eventType?: string;
    id?: string;
  };
  [FirebaseEventEnum.USER_PROFILE_UPDATE]: Record<string, unknown>;
  [FirebaseEventEnum.INITIALIZED_PAYMENT]: {
    method: "paystack" | "flutterwave" | "card";
  };
  [FirebaseEventEnum.PAYMENT_SUCCESS]: {
    method: "paystack" | "flutterwave" | "card";
  };
  [FirebaseEventEnum.PAYMENT_FAILURE]: {
    method: "paystack" | "flutterwave" | "card";
  };
  [FirebaseEventEnum.PAYMENT_CANCELLED]: {
    method: "paystack" | "flutterwave" | "card";
  };
  [FirebaseEventEnum.UPDATE_HEALTH_METRICS]: {
    metricsType: TMetricTypes;
    value?: number | string;
    unit?: TSelectedTypeUnits<TMetricTypes>;
    extra?: Record<string, unknown>;
  };
  [FirebaseEventEnum.UPDATE_HEALTH_PASSPORT]: {
    passportType: string;
    value: string;
    extra?: Record<string, unknown>;
  };
  [FirebaseEventEnum.UPDATE_PREGNANCY_CYCLE]: {
    ancDay?: string;
    expectedDeliveryDate?: string;
    id: string;
  };
  [FirebaseEventEnum.UPDATE_PREGNANCY_LOG_EVENT]: {
    event: string; // TODO: Update this type
    id: string;
  };
  [FirebaseEventEnum.HEALTH_METRICS_DELETE]: {
    metricsType: TMetricTypes;
    extra?: Record<string, unknown>;
  };
  [FirebaseEventEnum.HEALTH_PASSPORT_DELETE]: {
    passportType: string;
    extra?: Record<string, unknown>;
  };
  [FirebaseEventEnum.PREGNANCY_CYCLE_DELETE]: {
    id: string;
  };

  [FirebaseEventEnum.SELECT_PROGRAM_PLAN]: {
    selectedPlanId: string;
  };

  [FirebaseEventEnum.NEW_MESSAGE]: {
    chatRoomId: string;
    type: "incoming" | "outgoing";
  };

  [FirebaseEventEnum.NEW_CHAT_ROOM]: {
    chatRoomId: string;
  };

  [FirebaseEventEnum.READ_CHAT_MESSAGE]: {
    chatId: string;
  };

  [FirebaseEventEnum.NEW_INCOMING_NOTIFICATION]: undefined;
  [FirebaseEventEnum.REDEEM_VOUCHER]: undefined;
  [FirebaseEventEnum.RENEW_SUBSCRIPTION]: {
    userSubscriptionId: string;
    programId: string;
    planId: string;
    planName: string;
    programName: string;
  };
  [FirebaseEventEnum.SUBSCRIBE_PROGRAM]: {
    programId: string;
    planId: string;
  };
  [FirebaseEventEnum.KEM_CHAT_MESSAGE]: {
    timestamp: string;
    type: "incoming" | "outgoing";
    messageCharCount: number;
  };
  [FirebaseEventEnum.KEM_SPEECH_START_RECORDING]: {
    timestamp: string;
  };
  [FirebaseEventEnum.KEM_SPEECH_SEND_RECORDING]: {
    timestamp: string;
    duration_seconds: number;
  };
  [FirebaseEventEnum.KEM_INTERACTION_TIME]: {
    start_time: string;
    end_time: string;
    duration_in_mins: number;
    messages_sent: number;
  };
  [FirebaseEventEnum.UTM_SOURCE]: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  };
}
